import React from 'react';
import FlightIcon from '@material-ui/icons/Flight';
import { useGlobal } from '@rcg/ui-react';
import useStyles from './useStyles';
import useDictionary from '../../../../hooks/useDictionary';
import Dictionary from '../../../../utils/dictionary';
import TimeDuration, { timeWithLocale } from '../../../../utils/timeFormats';
import layoverFormats from '../../../../utils/layoverFormats';
import { ReactComponent as DefaultIcon } from '../../../../assets/icons/icon-missing-carrier.svg';

const FlightInformation = ({ flight }) => {
  const classes = useStyles();
  const {
    data: { localizedDayjs },
  } = useGlobal();
  const getDictionaryValue = useDictionary(Dictionary);

  const firstSegment = flight.flightSegments[0];
  const lastSegment = flight.flightSegments[flight.flightSegments.length - 1];
  const originCode = firstSegment.departure.airport.code;
  const destCode = lastSegment.arrival.airport.code;
  const segments = flight.flightSegments;

  const stopsLabel = (() => {
    if (segments.length === 1) {
      return getDictionaryValue(Dictionary.airNonstopLabel.key);
    }
    const stopCodes = segments
      .slice(0, -1)
      .map((s) => s.arrival.airport.code)
      .join(', ');
    if (segments.length === 2) {
      return `1 ${getDictionaryValue(Dictionary.airStopsNumberSingleLabel.key)} - ${stopCodes}`;
    }
    return `${segments.length - 1} ${getDictionaryValue(
      Dictionary.airStopsNumberLabel.key,
    )} - ${stopCodes}`;
  })();

  const dayOffset = layoverFormats.layoverDays({
    arrival: flight.arrivalDate,
    departure: flight.departDate,
  });

  const isCodeshare = firstSegment.marketingAirline.code !== firstSegment.operatingAirline.code;

  return (
    <div className={classes.flightRow}>
      <div className={classes.airlineBlock}>
        <object data={flight.flightIcon} className={classes.airlineLogo} type="image/png">
          <DefaultIcon className={classes.airlineLogo} />
        </object>
        <div className={classes.airlineText}>
          <p className={classes.airlineName}>
            {firstSegment.marketingAirline.name}
            <span className={classes.flightCode}>
              {` | ${firstSegment.marketingAirline.code} ${firstSegment.number}`}
            </span>
          </p>
          {(isCodeshare || firstSegment.airplane?.name) && (
            <p className={classes.operatedBy}>
              {isCodeshare &&
                `${getDictionaryValue(Dictionary.airOperatorLabel.key)} ${
                  firstSegment.operatingAirline.name
                } | `}
              {firstSegment.airplane?.name}
            </p>
          )}
        </div>
      </div>

      <span className={classes.divider} />

      <div className={classes.departureBlock}>
        <p className={classes.time}>{timeWithLocale(localizedDayjs, flight.departTime)}</p>
        <p className={classes.airportCode}>{originCode}</p>
      </div>

      <div className={classes.routeBlock}>
        <p className={classes.routeLabel}>{TimeDuration(flight.totalDurationMins)}</p>
        <div className={classes.routeLine}>
          <span className={classes.routeDot} />
          <span className={classes.line} />
          <FlightIcon className={classes.routeIcon} />
          <span className={classes.line} />
          <span className={classes.routeDot} />
        </div>
        <p className={classes.routeLabel}>{stopsLabel}</p>
      </div>

      <div className={classes.arrivalBlock}>
        <p className={classes.time}>
          {timeWithLocale(localizedDayjs, flight.arrivalTime)}
          {dayOffset && <sup className={classes.dayOffset}>{dayOffset}</sup>}
        </p>
        <p className={classes.airportCode}>{destCode}</p>
      </div>
    </div>
  );
};

export default FlightInformation;
